import { EFinancingCompany } from '@if/core/types'
import { createContext, useContext, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useUser } from './UserProvider'

interface TSelectedCompanyContext {
  selectedCompany: EFinancingCompany | null
  setSelectedCompany: (value: EFinancingCompany | null) => void
}

const SelectedCompanyContext = createContext<TSelectedCompanyContext | null>(null)

const LOCAL_STORAGE_KEY = 'selectedCompany'

const SelectedCompanyProvider = () => {
  const user = useUser()
  const defaultCompany = useMemo<EFinancingCompany | null>(() => {
    const cachedCompany = localStorage.getItem(LOCAL_STORAGE_KEY) as EFinancingCompany | null
    if (cachedCompany && user?.companyIds.includes(cachedCompany)) return cachedCompany
    return user?.companyIds?.at(0) ?? null
  }, [user])
  const [selectedCompany, setSelectedCompany] = useState<EFinancingCompany | null>(defaultCompany)

  const value = useMemo<TSelectedCompanyContext>(
    () => ({
      selectedCompany,
      setSelectedCompany: (value: EFinancingCompany | null) => {
        localStorage.setItem(LOCAL_STORAGE_KEY, value ?? '')
        setSelectedCompany(value)
      },
    }),
    [selectedCompany],
  )

  return (
    <SelectedCompanyContext.Provider value={value}>
      <Outlet />
    </SelectedCompanyContext.Provider>
  )
}

export const useSelectedCompany = () => {
  const ctx = useContext(SelectedCompanyContext)
  return ctx!.selectedCompany!
}

export const useSetSelectedCompany = () => {
  const ctx = useContext(SelectedCompanyContext)
  return ctx!.setSelectedCompany
}

export default SelectedCompanyProvider
